const defaultMakeBlockId = () => `block-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

export const toRenderableText = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) {
    return value.map((item) => toRenderableText(item)).filter(Boolean).join('\n').trim();
  }
  if (typeof value === 'object') {
    if (typeof value.text === 'string') return value.text;
    if (typeof value.content === 'string') return value.content;
    if (typeof value.summary === 'string') return value.summary;
    if (typeof value.encrypted_content === 'string') return value.encrypted_content;
    if (Array.isArray(value.content)) {
      return value.content
        .map((part) => toRenderableText(part?.text ?? part?.content ?? part))
        .filter(Boolean)
        .join('\n')
        .trim();
    }
    try {
      return JSON.stringify(value);
    } catch {
      return '';
    }
  }
  return '';
};

const collectReasoningText = (value) => {
  if (value === null || value === undefined) return [];
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    const text = String(value).trim();
    return text ? [text] : [];
  }
  if (Array.isArray(value)) {
    return value.flatMap((item) => collectReasoningText(item));
  }
  if (typeof value !== 'object') return [];

  const type = typeof value.type === 'string' ? value.type.trim().toLowerCase() : '';
  if (type === 'reasoning' || type === 'thinking') {
    return [
      ...collectReasoningText(value.summary),
      ...collectReasoningText(value.content),
      ...collectReasoningText(value.text),
    ];
  }
  if (type === 'summary_text' || type === 'reasoning_text') {
    return collectReasoningText(value.text);
  }

  if (Array.isArray(value.summary) || Array.isArray(value.content)) {
    return [
      ...collectReasoningText(value.summary),
      ...collectReasoningText(value.content),
      ...collectReasoningText(value.text),
    ];
  }
  return [];
};

export const extractThinkingText = (value) => {
  const extracted = collectReasoningText(value)
    .map((item) => String(item || '').trim())
    .filter(Boolean);
  if (extracted.length > 0) {
    return extracted.join('\n').trim();
  }
  return typeof value === 'string' ? value : '';
};

export const toolCallIdsMatch = (left, right) => {
  if (left == null || right == null) return left == null && right == null;
  return String(left) === String(right);
};

/** Mark any in-flight tool/sub-agent blocks as completed once the turn is done. */
export const finalizeRunningProcessBlocks = (blocks) => {
  if (!Array.isArray(blocks)) return [];
  return blocks.map((block) => {
    if (!block || typeof block !== 'object') return block;
    if (block.type === 'tool_call' && block.status === 'running') {
      return { ...block, status: 'completed' };
    }
    if (block.type === 'sub_agent' && block.status === 'running') {
      return {
        ...block,
        status: 'completed',
        blocks: finalizeRunningProcessBlocks(block.blocks || []),
      };
    }
    if (block.type === 'sub_agent' && Array.isArray(block.blocks) && block.blocks.length > 0) {
      return { ...block, blocks: finalizeRunningProcessBlocks(block.blocks) };
    }
    return block;
  });
};

export const normalizeTimelineBlock = (block, makeBlockId = defaultMakeBlockId) => {
  if (!block) return null;
  const payload = block.payload || {};
  const rawType = block.type || block.block_type || payload.type || payload.block_type || null;
  if (!rawType) return null;
  const blockType = String(rawType).toLowerCase();

  if (blockType === 'text') {
    return { id: makeBlockId(), type: 'text', text: toRenderableText(block.text ?? payload.text) };
  }
  if (blockType === 'thinking') {
    const text = extractThinkingText(payload.data ?? payload.text ?? block.text);
    if (!text) return null;
    return { id: makeBlockId(), type: 'thinking', text };
  }
  if (blockType === 'tool_use') {
    return {
      id: makeBlockId(),
      type: 'tool_call',
      toolName: block.tool_name || payload.tool_name || payload.toolName || 'tool',
      toolCallId: block.tool_call_id || payload.tool_call_id || payload.toolCallId || null,
      args: payload.input || payload.args || {},
      reasoning: payload.reasoning || '',
      status: payload.status || 'completed',
      result: null,
    };
  }
  if (blockType === 'tool_result') {
    return {
      id: makeBlockId(),
      type: 'tool_result',
      toolName: block.tool_name || payload.tool_name || payload.toolName || 'tool',
      toolCallId: block.tool_call_id || payload.tool_call_id || payload.toolCallId || null,
      result: payload.output ?? payload.result ?? payload,
      isError: Boolean(payload.is_error || payload.isError),
    };
  }
  if (blockType === 'attachment') {
    const fileRef = payload.file_ref || payload.fileRef || {};
    const filename = fileRef.filename || payload.filename || 'attachment';
    return {
      id: makeBlockId(),
      type: 'attachment',
      filename,
      contentType: fileRef.content_type || payload.content_type || null,
      sizeBytes: fileRef.size_bytes || payload.size_bytes || null,
      attachmentId: payload.attachment_id || payload.attachmentId || null,
      downloadPath: payload.download_path || payload.downloadPath || null,
      label: payload.label || fileRef.label || null,
      fileRef: Object.keys(fileRef).length ? fileRef : null,
    };
  }
  if (blockType === 'agent_card') {
    // Posted by an agent between turns: a question, update or approval for this person.
    const card = { ...payload, ...block };
    delete card.payload;
    delete card.block_type;
    return { id: makeBlockId(), type: 'agent_card', card };
  }
  if (blockType === 'notice') {
    return {
      id: makeBlockId(),
      type: 'notice',
      text: toRenderableText(block.text ?? payload.text),
      meta: {
        compactionId: payload.compaction_id ?? payload.compactionId ?? null,
        collapsedMessages: payload.collapsed_messages ?? payload.collapsedMessages ?? null,
        summaryChars: payload.summary_chars ?? payload.summaryChars ?? null,
        beforeTokensEst: payload.before_tokens_est ?? payload.beforeTokensEst ?? null,
        afterTokensEst: payload.after_tokens_est ?? payload.afterTokensEst ?? null,
        childSessionId: payload.child_session_id ?? payload.childSessionId ?? null,
        parentSessionId: payload.parent_session_id ?? payload.parentSessionId ?? null,
      },
    };
  }
  return null;
};
